import type { ReactElement } from "react";
import { cloneElement, useLayoutEffect, useRef } from "react";
import { PageContext } from "./PageContext.tsx";
import { useImpressionTracking } from "./use-impression-tracking.ts";

export type PageProps = {
  /**
   * Optional component to render instead of a div. If provided, the component
   * must accept a `ref` prop.
   */
  as?: ReactElement<{
    ref?: React.Ref<HTMLElement>;
    children?: React.ReactNode;
    "data-page-name"?: string;
  }>;
  /**
   * The name of the current page. (Used for analytics.)
   */
  name: string;
  children: React.ReactNode | React.ReactNode[];
};

export const Page = ({ as, name, children }: PageProps) => {
  const setImpressionTarget = useImpressionTracking("PAGE_VIEW", {
    properties: { page: name },
  });

  // The default wrapper is `display: contents` so it stays layout-neutral and
  // doesn't disturb the page's own fullscreen layout. But a `display: contents`
  // element generates no box, so an IntersectionObserver placed on it never
  // reports as intersecting and PAGE_VIEW never fires (P3-114). Observe the
  // wrapper's content-root child — a real box — instead of the wrapper itself.
  // When a caller supplies their own `as` element (which generates a box),
  // observe it directly.
  const usingDefaultWrapper = as === undefined;
  const wrapperRef = useRef<HTMLElement | null>(null);

  // Resolve the observed target after every commit so a page whose content root
  // mounts on a later render (e.g. after an async load) still gets observed.
  // `setImpressionTarget` is a state setter, so re-setting the same node is a
  // no-op and won't churn the observer.
  useLayoutEffect(() => {
    const node = wrapperRef.current;
    if (!usingDefaultWrapper) {
      setImpressionTarget(node);
      return;
    }
    const child = node?.firstElementChild ?? null;
    setImpressionTarget(child instanceof HTMLElement ? child : null);
  });

  const element = as ?? <div style={{ display: "contents" }} />;

  return (
    <PageContext name={name}>
      {cloneElement(element, {
        ref: wrapperRef,
        children,
        "data-page-name": name,
      })}
    </PageContext>
  );
};
